import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePodcastTranscription } from '../../hooks/usePodcastTranscription';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX,
  Share2,
  MessageCircle,
  Globe,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Clock,
  Calendar,
  Headphones,
  BookOpen,
  Languages,
  Forward,
  Rewind,
  Heart,
  Download,
  ExternalLink,
  Sparkles,
  Music,
  Mic,
  Zap,
  Star
} from 'lucide-react';
import { usePodcasts, type PodcastWithRelations } from '../../hooks/usePodcasts';
import { useAudioPlayer } from './hooks/useAudioPlayer';
import { AudioPlayer } from './components/AudioPlayer';

interface Chapter {
  time: string;
  title: string;
  description?: string;
}

interface Comment {
  id: string;
  user: {
    name: string;
    avatar: string;
  };
  content: string;
  timestamp: string;
  likes: number;
}

const PodcastEpisode = () => {
  const { id } = useParams<{ id: string }>();
  const [podcast, setPodcast] = useState<PodcastWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<'pt' | 'en'>('pt');
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [transcription, setTranscription] = useState<string | null>(null);
  const [chapters, setChapters] = useState<PodcastChapter[]>([]);
  const [newComment, setNewComment] = useState('');
  const [relatedPodcasts, setRelatedPodcasts] = useState<PodcastWithRelations[]>([]);
  const [currentChapter, setCurrentChapter] = useState<number>(0);

  const { 
    fetchTranscription,
    fetchChapters,
    generateTranscription,
    generateChapters
  } = usePodcastTranscription();

  const { 
    currentPodcast,
    isPlaying,
    loadingAudio,
    handlePlayPause,
    audioControls
  } = useAudioPlayer();

  const { fetchPodcasts } = usePodcasts();
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadPodcast();
  }, [id]);

  const loadPodcast = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const podcasts = await fetchPodcasts();
      const currentPodcast = podcasts.find(p => p.id === id);
      
      if (!currentPodcast) {
        throw new Error('Podcast não encontrado');
      }

      setPodcast(currentPodcast);
      
      // Load or generate transcription and chapters
      try {
        const transcriptionData = await fetchTranscription(currentPodcast.id);
        setTranscription(transcriptionData.content);
      } catch (err) {
        console.log('No transcription found, generating...');
        const newTranscription = await generateTranscription(currentPodcast.id, currentPodcast.audio_url);
        setTranscription(newTranscription.content);
      }

      try {
        const chaptersData = await fetchChapters(currentPodcast.id);
        setChapters(chaptersData);
      } catch (err) {
        console.log('No chapters found, generating...');
        if (transcription) {
          const newChapters = await generateChapters(currentPodcast.id, transcription);
          setChapters(newChapters);
        }
      }

      // Load related podcasts
      const related = podcasts
        .filter(p => p.id !== id && p.category_id === currentPodcast.category_id)
        .slice(0, 3);
      setRelatedPodcasts(related);

      // Load sample comments
      setComments([
        {
          id: '1',
          user: { name: 'João Silva', avatar: 'https://i.pravatar.cc/150?u=1' },
          content: 'Excelente episódio! Muito informativo.',
          timestamp: '2 horas atrás',
          likes: 5
        },
        {
          id: '2',
          user: { name: 'Maria Santos', avatar: 'https://i.pravatar.cc/150?u=2' },
          content: 'Adorei a discussão sobre este tema.',
          timestamp: '5 horas atrás',
          likes: 3
        }
      ]);

    } catch (error) {
      console.error('Error loading podcast:', error);
      setError(error instanceof Error ? error.message : 'Erro ao carregar podcast');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!podcast) return;

    try {
      await navigator.share({
        title: podcast.title,
        text: podcast.description || '',
        url: window.location.href
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const comment: Comment = {
      id: Date.now().toString(),
      user: {
        name: 'Você',
        avatar: 'https://i.pravatar.cc/150?u=me'
      },
      content: newComment,
      timestamp: 'Agora mesmo',
      likes: 0
    };

    setComments(prev => [comment, ...prev]);
    setNewComment('');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-gradient-to-b from-background via-background/90 to-primary/5">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Music className="w-8 h-8 text-primary animate-pulse" />
          </div>
          <div className="mt-6 text-center text-primary font-medium">Carregando episódio...</div>
        </div>
      </div>
    );
  }

  if (error || !podcast) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-gradient-to-b from-background via-background/90 to-destructive/5">
        <div className="text-center max-w-md p-8 rounded-xl bg-card/30 backdrop-blur-md border border-white/10 shadow-xl">
          <h1 className="text-2xl font-bold mb-4 text-destructive">Episódio não encontrado</h1>
          <p className="mb-6 text-gray-400">Não foi possível encontrar o episódio solicitado.</p>
          <Link 
            to="/podcasts" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/20"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar aos podcasts
          </Link>
        </div>
      </div>
    );
  }

  // Get speaker style based on speaker name
  const getSpeakerStyle = (speaker: string) => {
    const baseStyle = "flex flex-col md:flex-row gap-6 border-b border-white/10 pb-6 hover:bg-white/5 rounded-lg p-4 transition-all duration-300";
    
    switch (speaker.toLowerCase()) {
      case 'intro':
      case 'introdução':
        return `${baseStyle} bg-gradient-to-r from-purple-500/10 to-primary/5 hover:from-purple-500/20 hover:to-primary/10`;
      case 'entrevistador':
      case 'host':
      case 'apresentador':
        return `${baseStyle} bg-gradient-to-r from-blue-500/10 to-cyan-500/5 hover:from-blue-500/20 hover:to-cyan-500/10`;
      case 'convidado':
      case 'guest':
        return `${baseStyle} bg-gradient-to-r from-green-500/10 to-emerald-500/5 hover:from-green-500/20 hover:to-emerald-500/10`;
      case 'conclusão':
      case 'conclusion':
      case 'outro':
        return `${baseStyle} bg-gradient-to-r from-amber-500/10 to-orange-500/5 hover:from-amber-500/20 hover:to-orange-500/10`;
      default:
        return `${baseStyle} bg-gradient-to-r from-gray-500/10 to-slate-500/5 hover:from-gray-500/20 hover:to-slate-500/10`;
    }
  };

  // Get speaker icon based on speaker name
  const getSpeakerIcon = (speaker: string) => {
    switch (speaker.toLowerCase()) {
      case 'intro':
      case 'introdução':
        return <Sparkles className="w-5 h-5 text-purple-400" />;
      case 'entrevistador':
      case 'host':
      case 'apresentador':
        return <Mic className="w-5 h-5 text-blue-400" />;
      case 'convidado':
      case 'guest':
        return <Star className="w-5 h-5 text-green-400" />;
      case 'conclusão':
      case 'conclusion':
      case 'outro':
        return <Zap className="w-5 h-5 text-amber-400" />;
      default:
        return <Music className="w-5 h-5 text-gray-400" />;
    }
  };

  // Get speaker text color based on speaker name
  const getSpeakerTextColor = (speaker: string) => {
    switch (speaker.toLowerCase()) {
      case 'intro':
      case 'introdução':
        return "from-purple-400 to-primary";
      case 'entrevistador':
      case 'host':
      case 'apresentador':
        return "from-blue-400 to-cyan-400";
      case 'convidado':
      case 'guest':
        return "from-green-400 to-emerald-400";
      case 'conclusão':
      case 'conclusion':
      case 'outro':
        return "from-amber-400 to-orange-400";
      default:
        return "from-gray-200 to-white";
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-background via-background/95 to-primary/5">
      {/* Hero Section */}
      <div className="relative h-[70vh] overflow-hidden">
        <motion.div
          initial={{ scale: 1.1, opacity: 0.5 }}
          animate={{ scale: 1, opacity: 0.8 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${podcast.image_url})`,
            filter: 'blur(8px)',
            transform: 'scale(1.05)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/80 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent opacity-60" />
        
        <div className="relative h-full container mx-auto px-4 flex flex-col justify-end pb-20">
          <Link
            to="/podcasts"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-primary transition-colors mb-8 w-fit group"
          >
            <span className="bg-secondary/30 p-1.5 rounded-full group-hover:bg-primary/20 transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </span>
            <span>Voltar aos podcasts</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="px-4 py-1.5 bg-primary/20 backdrop-blur-sm rounded-full text-primary border border-primary/20 shadow-sm shadow-primary/10">
                {podcast.category?.name}
              </span>
              <span className="flex items-center gap-2 text-sm text-gray-300 bg-white/5 px-3 py-1.5 rounded-full backdrop-blur-sm">
                <Calendar className="w-4 h-4 text-primary/70" />
                {new Date(podcast.published_at || '').toLocaleDateString('pt-PT')}
              </span>
              <span className="flex items-center gap-2 text-sm text-gray-300 bg-white/5 px-3 py-1.5 rounded-full backdrop-blur-sm">
                <Clock className="w-4 h-4 text-primary/70" />
                {podcast.duration}
              </span>
              <span className="flex items-center gap-2 text-sm text-gray-300 bg-white/5 px-3 py-1.5 rounded-full backdrop-blur-sm">
                <Headphones className="w-4 h-4 text-primary/70" />
                {podcast.plays} plays
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">{podcast.title}</h1>
            <p className="text-xl text-gray-300 max-w-3xl mb-8 leading-relaxed">{podcast.description}</p>

            <div className="flex flex-wrap items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(124, 58, 237, 0.5)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handlePlayPause(podcast)}
                disabled={loadingAudio && currentPodcast?.id !== podcast.id}
                className="flex items-center gap-2 md:gap-3 px-4 md:px-8 py-3 md:py-4 bg-gradient-to-r from-primary to-purple-600 text-primary-foreground rounded-full hover:from-primary/90 hover:to-purple-500 transition-all duration-300 disabled:opacity-50 shadow-lg shadow-primary/20 w-full md:w-auto"
              >
                {loadingAudio && currentPodcast?.id === podcast.id ? (
                  <div className="w-6 h-6 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : currentPodcast?.id === podcast.id && isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6" />
                )}
                {loadingAudio && currentPodcast?.id === podcast.id
                  ? 'Carregando...'
                  : currentPodcast?.id === podcast.id && isPlaying
                  ? 'Pausar'
                  : <span className="whitespace-nowrap">Ouvir Episódio ✨</span>
                }
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(124, 58, 237, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsLiked(!isLiked)}
                className={`p-4 rounded-full transition-all duration-300 ${
                  isLiked 
                    ? 'bg-primary/20 text-primary border border-primary/30 shadow-md shadow-primary/20' 
                    : 'bg-secondary/80 hover:bg-secondary border border-white/5 shadow-md'
                }`}
              >
                <Heart className={isLiked ? 'fill-primary' : ''} />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(124, 58, 237, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                onClick={handleShare}
                className="p-4 bg-secondary/80 hover:bg-secondary rounded-full transition-all duration-300 border border-white/5 shadow-md"
              >
                <Share2 className="w-6 h-6" />
              </motion.button>

              <motion.a
                href={podcast.audio_url}
                download
                whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(124, 58, 237, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="p-4 bg-secondary/80 hover:bg-secondary rounded-full transition-all duration-300 border border-white/5 shadow-md"
              >
                <Download className="w-6 h-6" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-[2fr,1fr] gap-12">
          <div>
            {/* Chapters */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">Capítulos</h2>
                <div className="h-px flex-grow bg-gradient-to-r from-primary/50 to-transparent"></div>
              </div>
              <div className="space-y-4">
                {chapters.map((chapter, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.02, boxShadow: '0 0 15px rgba(124, 58, 237, 0.2)' }}
                    onClick={() => {
                      setCurrentChapter(index);
                      if (currentPodcast?.id === podcast.id) {
                        // Convert interval to seconds and update audio position
                        const timeMatch = chapter.start_time.match(/(d+):(d+):(d+)/);
                        if (timeMatch) {
                          const [_, hours, minutes, seconds] = timeMatch;
                          const totalSeconds = Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
                          audioControls.handleTimeUpdate(totalSeconds);
                        }
                      }
                    }}
                    className={`w-full p-5 rounded-xl transition-all duration-300 border ${currentChapter === index
                      ? 'bg-gradient-to-r from-primary/20 to-purple-500/10 border-primary/30 shadow-lg shadow-primary/10'
                      : 'bg-secondary/50 hover:bg-secondary/70 border-white/5'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-mono bg-black/20 px-3 py-1.5 rounded-full text-primary">{chapter.start_time}</span>
                        <span className="font-medium text-lg">{chapter.title}</span>
                      </div>
                      {chapter.description && (
                        <span className="text-sm text-gray-400 max-w-xs truncate hidden md:block">{chapter.description}</span>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Transcript */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">Transcrição</h2>
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="bg-gradient-to-r from-primary/20 to-purple-500/20 text-primary text-xs px-4 py-1.5 rounded-full flex items-center gap-1 border border-primary/20 shadow-sm shadow-primary/10"
                  >
                    <span className="font-medium">powered by</span> 
                    <span className="font-bold">SinopseIA</span>
                  </motion.div>
                  <div className="h-px flex-grow bg-gradient-to-r from-primary/50 to-transparent"></div>
                </div>
                <div className="flex items-center gap-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentLanguage(currentLanguage === 'pt' ? 'en' : 'pt')}
                    className="flex items-center gap-2 px-4 py-2 bg-secondary/80 hover:bg-secondary rounded-lg transition-all duration-300 border border-white/5 shadow-md"
                  >
                    <Globe className="w-4 h-4 text-primary/70" />
                    {currentLanguage === 'pt' ? 'PT' : 'EN'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowTranscript(!showTranscript)}
                    className="flex items-center gap-2 px-4 py-2 bg-secondary/80 hover:bg-secondary rounded-lg transition-all duration-300 border border-white/5 shadow-md"
                  >
                    <BookOpen className="w-4 h-4 text-primary/70" />
                    {showTranscript ? 'Ocultar' : 'Mostrar'} Transcrição
                    {showTranscript ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </motion.button>
                </div>
              </div>

              <AnimatePresence>
                {showTranscript && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div
                      ref={transcriptRef}
                      className="bg-gradient-to-br from-secondary/70 via-primary/10 to-purple-500/30 backdrop-blur-md rounded-xl p-8 prose prose-invert max-w-none shadow-2xl border border-white/10 relative overflow-hidden"
                      style={{
                        backgroundSize: '200% 200%',
                        animation: 'gradientAnimation 15s ease infinite'
                      }}
                    >
                      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary via-purple-500 to-primary animate-gradient-x"></div>
                      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl opacity-30 animate-pulse-slow"></div>
                      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl opacity-30 animate-pulse-slow animation-delay-2000"></div>
                      <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/10 relative z-10">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-primary rounded-full animate-pulse shadow-lg shadow-primary/30"></div>
                          <span className="text-sm font-medium bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-500 animate-text-shimmer">Transcrição automática</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold px-3 py-1.5 bg-primary/20 rounded-full text-primary shadow-inner border border-primary/20">SinopseIA</span>
                        </div>
                      </div>
                      
                      {transcription ? (
                        <div className="space-y-8 relative z-10">
                          {transcription.split('\n\n').map((segment, index) => {
                            const [header, ...contentLines] = segment.split('\n');
                            const [speaker, timestamp] = header.split(' | ');
                            const content = contentLines.join('\n');
                            // Format speaker name
                            const formattedSpeaker = speaker.charAt(0).toUpperCase() + speaker.slice(1).toLowerCase();
                            
                            return (
                              <motion.div 
                                key={index} 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
                                whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
                                className={getSpeakerStyle(formattedSpeaker)}
                              >
                                <div className="md:w-1/4 flex flex-col relative">
                                  <div className="flex items-center gap-2 mb-2">
                                    {getSpeakerIcon(formattedSpeaker)}
                                    <span className="text-lg font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r ${getSpeakerTextColor(formattedSpeaker)}">
                                      {formattedSpeaker}
                                    </span>
                                  </div>
                                  <span className="text-xs bg-black/20 px-3 py-1.5 rounded-full text-gray-300 w-fit backdrop-blur-sm shadow-inner flex items-center gap-1.5 border border-white/5">
                                    <Clock className="w-3 h-3 opacity-70" />
                                    {timestamp}
                                  </span>
                                  <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-1.5 h-16 bg-gradient-to-b from-primary/80 via-purple-500/50 to-transparent rounded-full hidden md:block"></div>
                                </div>
                                <div className="md:w-3/4 transcription-content relative">
                                  <p className="text-gray-200 leading-relaxed backdrop-blur-sm bg-gradient-to-br from-black/10 to-white/5 p-5 rounded-lg shadow-inner hover:shadow-lg transition-all duration-300 border border-white/5">{content}</p>
                                  <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-1.5 h-20 bg-gradient-to-b from-purple-500/50 via-secondary/30 to-transparent rounded-full hidden md:block"></div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12">
                          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                          <p className="text-gray-400">
                            A Gerar a transcrição automática...
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Comments Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">Comentários</h2>
                <div className="h-px flex-grow bg-gradient-to-r from-primary/50 to-transparent"></div>
              </div>
              
              <form onSubmit={handleCommentSubmit} className="mb-8">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Deixe seu comentário..."
                  className="w-full px-4 py-3 bg-secondary/50 backdrop-blur-sm rounded-lg focus:ring-2 focus:ring-primary focus:outline-none resize-none border border-white/5 shadow-inner"
                  rows={3}
                />
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: '0 0 15px rgba(124, 58, 237, 0.3)' }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="mt-3 px-6 py-2.5 bg-gradient-to-r from-primary to-purple-600 text-primary-foreground rounded-lg hover:from-primary/90 hover:to-purple-500 transition-all shadow-md shadow-primary/10 font-medium"
                >
                  Comentar
                </motion.button>
              </form>

              <div className="space-y-6">
                {comments.map((comment) => (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    className="bg-gradient-to-br from-secondary/50 to-primary/5 backdrop-blur-sm rounded-xl p-5 border border-white/5 shadow-lg hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <img
                          src={comment.user.avatar}
                          alt={comment.user.name}
                          className="w-12 h-12 rounded-full border-2 border-primary/30"
                        />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-background">
                          <MessageCircle className="w-3 h-3 text-primary-foreground" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-white">{comment.user.name}</span>
                          <span className="text-sm text-gray-400 bg-black/20 px-2 py-1 rounded-full">{comment.timestamp}</span>
                        </div>
                        <p className="text-gray-200 mb-3">{comment.content}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <motion.button 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="text-sm text-gray-400 hover:text-primary transition-colors flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            Responder
                          </motion.button>
                          <motion.button 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="text-sm text-gray-400 hover:text-primary transition-colors flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full"
                          >
                            <Heart className="w-3.5 h-3.5" />
                            {comment.likes} curtidas
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Related Episodes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">Episódios Relacionados</h3>
                <div className="h-px flex-grow bg-gradient-to-r from-primary/50 to-transparent"></div>
              </div>
              <div className="space-y-5">
                {relatedPodcasts.map((relatedPodcast) => (
                  <Link
                    key={relatedPodcast.id}
                    to={`/podcasts/${relatedPodcast.id}`}
                    className="block group"
                  >
                    <motion.div
                      whileHover={{ y: -8, scale: 1.02 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      className="bg-gradient-to-br from-secondary/50 via-secondary/30 to-primary/5 rounded-xl overflow-hidden border border-white/5 shadow-lg hover:shadow-xl hover:shadow-primary/10 transition-all duration-300"
                    >
                      <div className="aspect-video relative">
                        <img
                          src={relatedPodcast.image_url}
                          alt={relatedPodcast.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent group-hover:opacity-80 transition-opacity" />
                        <div className="absolute bottom-3 right-3 p-2.5 bg-primary text-primary-foreground rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg shadow-primary/30 transform translate-y-2 group-hover:translate-y-0">
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </div>
                      <div className="p-5">
                        <h4 className="font-medium text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {relatedPodcast.title}
                        </h4>
                        <div className="flex items-center justify-between text-sm text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-primary/70" />
                            {relatedPodcast.duration}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Headphones className="w-3.5 h-3.5 text-primary/70" />
                            {relatedPodcast.plays} plays
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Tags */}
            {podcast.tags && podcast.tags.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-gradient-to-br from-secondary/30 to-primary/5 backdrop-blur-sm rounded-xl p-5 border border-white/5 shadow-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">Tags</h3>
                  <div className="h-px flex-grow bg-gradient-to-r from-primary/50 to-transparent"></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {podcast.tags.map((tag) => (
                    <motion.div
                      key={tag.id}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        to={`/podcasts?tag=${tag.slug}`}
                        className="px-3 py-1.5 bg-gradient-to-r from-primary/20 to-purple-500/10 hover:from-primary/30 hover:to-purple-500/20 rounded-full text-sm transition-all duration-300 border border-primary/20 shadow-sm hover:shadow-md hover:shadow-primary/10 flex items-center gap-1.5"
                      >
                        <span className="w-2 h-2 rounded-full bg-primary"></span>
                        {tag.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Audio Player */}
      {currentPodcast?.id === podcast.id && (
        <AudioPlayer
          podcast={podcast}
          isPlaying={isPlaying}
          controls={audioControls}
          onPlayPause={handlePlayPause}
        />
      )}
    </div>
  );
};

export default PodcastEpisode;

